import {useMemo, useEffect} from 'react';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {
    selectIsNonSupply,
    selectIsNonSupplyCarcase,
} from 'components/customer/Materials/store/materialSlice';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {useProductContext} from 'contexts';
import {setBaseInnerDrawerFaceHeight} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {mapData} from 'components/customer/Product/Description/mapData';
import {
    CabinetDetailsInterface,
    Product,
} from 'components/customer/Product/entity/Product';
import {
    Drawer,
    Part,
    Hardware,
} from 'components/customer/Product/entity/Drawer';
import {Shelf} from 'components/customer/Product/entity/Shelf';
import {Structure} from 'components/customer/Product/entity/Structure';
import {InitialValues} from 'components/customer/Product/helpers/mapDefaults';
import {getIsInnerDrawer} from 'components/customer/Product/helpers/getFormSchema';

type ProductInclusionType = {
    title: string;
    hidden?: boolean;
    items: string[];
};

interface useProductInclusionProps {
    formValues: InitialValues;
    cabinetDetails: CabinetDetailsInterface;
    drillOnlyHingeDetails: {
        drill_only_hinge: boolean;
        name: string;
        is_available_for_room: boolean;
    };
    productData: Product;
    productConfig: Structure[];
}

const useProductInclusion = ({
    formValues,
    cabinetDetails,
    drillOnlyHingeDetails: drillOnlyHingeDetails,
    productData,
    productConfig,
}: useProductInclusionProps) => {
    const dispatch = useAppDispatch();

    const selectedDrawers = formValues.drawers;
    const drawerAmount = formValues.drawer_amount;
    const selectedTopId = formValues.cabinet_top;
    const selectedHinge = {
        drill_only_hinge_name: formValues.drill_only_hinge_name,
        door_hinge_amount: formValues.door_hinge_amount,
        door_hinge_amount_1: formValues.door_hinge_amount_1,
    };
    const selectedShelves = formValues.shelves;
    const hasAdjustableLegs = formValues.cabinet_adjustable_legs;
    const shelfAmount = formValues.simple_shelves_amount;
    const shelvesPosition = {
        ...(formValues.cabinet_hori_shelves && {
            cabinet_hori_shelves: formValues.cabinet_hori_shelves,
        }),
        ...(formValues.cabinet_vert_shelves && {
            cabinet_vert_shelves: formValues.cabinet_vert_shelves,
        }),
    };
    const shelvesVertical = {
        ...(formValues.upper_shelves && {
            upper_shelves: formValues.upper_shelves,
        }),
        ...(formValues.lower_shelves && {
            lower_shelves: formValues.lower_shelves,
        }),
        ...(formValues.middle_shelves && {
            middle_shelves: formValues.middle_shelves,
        }),
    };
    const hasSimpleShelves = formValues.cabinet_simple_shelves;
    const cabinetWidth = {
        cabinet_width: formValues.cabinet_width,
        cabinet_left_width: formValues.cabinet_left_width,
        cabinet_right_width: formValues.cabinet_right_width,
    };
    const cabinetDepth = {
        cabinet_depth: formValues.cabinet_depth,
        cabinet_left_depth: formValues.cabinet_left_depth,
        cabinet_right_depth: formValues.cabinet_right_depth,
    };

    const isNonSupply = useAppSelector(selectIsNonSupply);
    const isNonSupplyCarcase = useAppSelector(selectIsNonSupplyCarcase);
    const door = useAppSelector(getDoor);

    const {getProductInfo, setAdjustableLegsQuantity} = useProductContext<{
        setAdjustableLegsQuantity: (quantity: number) => void;
        getProductInfo: (value: string) => string;
    }>();
    const productName = getProductInfo('name');

    const supplyHardware = formValues.hasOwnProperty('cabinet_include_hardware')
        ? formValues.cabinet_include_hardware
        : false;

    const drawers =
        selectedDrawers
            ?.map((sd) =>
                cabinetDetails?.drawers?.find((d) => {
                    const drawerType = sd.drawer_type ?? sd.insert_id;
                    return d.id == drawerType;
                })
            )
            .filter((sd) => sd) || [];

    const isInnerDrawer = useMemo(
        () => getIsInnerDrawer(productConfig),
        [productConfig]
    );

    let groupedDrawers = new Map<number, Drawer[]>();
    let groupedParts = new Map<string, Part[]>();
    let groupedHardwares = new Map<string, Hardware[]>();
    let groupedShelves = new Map<number, Shelf[]>();
    let groupedProductHardwares = new Map<string, Hardware[]>();
    let groupedProductParts = new Map<string, Part[]>();

    if (drawers) {
        groupedDrawers = mapData('id', drawers);

        const parts: Part[] = [];
        const hardwares: Hardware[] = [];
        drawers.forEach((drawer: Drawer) => {
            if (drawer?.parts) {
                parts.push(...drawer.parts);
            }

            if (drawer?.hardware) {
                hardwares.push(...drawer.hardware);
            }
        });

        groupedParts = mapData(
            'partId',
            parts.map((part) => ({
                ...part,
                partName: part.part_name ? part.part_name : part.partName,
                partId: part.part_id ? part.part_id : part.partId,
            }))
        );

        groupedHardwares = mapData(
            'hardwareId',
            hardwares.map((hardware) => ({
                ...hardware,
                hardwareId: String(
                    hardware.hardware_id
                        ? hardware.hardware_id
                        : hardware.hardwareId
                ),
                hardwareName: hardware.hardware_name
                    ? hardware.hardware_name
                    : hardware.hardwareName,
            }))
        );
    }

    if (selectedShelves) {
        groupedShelves = mapData('shelf_type', selectedShelves);
    }

    if (cabinetDetails?.hardware) {
        groupedProductHardwares = mapData(
            'hardwareId',
            cabinetDetails.hardware.map((hardware) => ({
                ...hardware,
                hardwareId: String(
                    hardware.hardware_id
                        ? hardware.hardware_id
                        : hardware.hardwareId
                ),
                hardwareName: hardware.hardware_name
                    ? hardware.hardware_name
                    : hardware.hardwareName,
            }))
        );
    }

    if (cabinetDetails?.parts) {
        groupedProductParts = mapData(
            'partId',
            cabinetDetails.parts
                .filter((p: Part) => p.part_name || p.partName)
                .map((part) => ({
                    ...part,
                    partName: part.part_name ? part.part_name : part.partName,
                    partId: part.part_id ? part.part_id : part.partId,
                }))
        );
    }

    const getShelvesVertical = (): string[] => [
        ...(shelvesVertical?.upper_shelves?.length
            ? [
                  `- ${shelvesVertical?.upper_shelves?.length} x ${
                      shelvesVertical?.upper_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Upper)`,
              ]
            : []),
        ...(shelvesVertical?.middle_shelves?.length
            ? [
                  `- ${shelvesVertical?.middle_shelves?.length} x ${
                      shelvesVertical?.middle_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Middle)`,
              ]
            : []),
        ...(shelvesVertical?.lower_shelves?.length
            ? [
                  `- ${shelvesVertical?.lower_shelves?.length} x ${
                      shelvesVertical?.lower_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Lower)`,
              ]
            : []),
    ];

    const getShelvesVerticalComplex = (): string[] => [
        ...(shelvesVertical?.upper_shelves?.length
            ? [
                  `- ${shelvesVertical?.upper_shelves?.length} x ${
                      shelvesVertical?.upper_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Upper)`,
              ]
            : []),
        ...(shelvesVertical?.middle_shelves?.length
            ? [
                  `- ${shelvesVertical?.middle_shelves?.length} x ${
                      shelvesVertical?.middle_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Middle)`,
              ]
            : []),
        ...(shelvesVertical?.lower_shelves?.length
            ? [
                  `- ${shelvesVertical?.lower_shelves?.length} x ${
                      shelvesVertical?.lower_shelves?.length > 1
                          ? 'Shelves'
                          : 'Shelf'
                  } (Lower - Bottom Left)`,
              ]
            : []),
        ...(selectedShelves?.length > 0
            ? [
                  `- ${selectedShelves.length} x ${
                      selectedShelves.length > 1 ? 'Shelves' : 'Shelf'
                  } (Lower - Bottom Right)`,
              ]
            : []),
    ];

    const parseHardwareQuantity = (productHardwares: Hardware[]) => {
        if (!productHardwares) return null;
        let quantity = 0;

        productHardwares.forEach((p: Hardware) => {
            const conditionalType = Number(
                p.conditional_type || p.conditionalType
            );
            const conditionalValue = Number(
                p.conditional_value || p.conditionalValue
            );

            if (conditionalType <= 0 && conditionalValue <= 0)
                quantity += parseInt(p.quantity);
            else {
                if (conditionalType == 1) {
                    const cabinetWidthFinal =
                        cabinetWidth && cabinetWidth?.cabinet_width
                            ? cabinetWidth?.cabinet_width
                            : cabinetWidth?.cabinet_left_width >
                                cabinetWidth?.cabinet_right_width
                              ? cabinetWidth?.cabinet_left_width
                              : cabinetWidth?.cabinet_right_width;

                    quantity += cabinetWidthFinal
                        ? (Math.floor(cabinetWidthFinal / conditionalValue) ||
                              0) * parseInt(p.quantity)
                        : 0;
                }

                if (conditionalType == 2) {
                    const cabinetDepthFinal =
                        cabinetDepth && cabinetDepth?.cabinet_depth
                            ? cabinetDepth?.cabinet_depth
                            : cabinetDepth?.cabinet_left_depth >
                                cabinetDepth?.cabinet_right_depth
                              ? cabinetDepth?.cabinet_left_depth
                              : cabinetDepth?.cabinet_right_depth;
                    quantity += cabinetDepthFinal
                        ? (Math.floor(cabinetDepthFinal / conditionalValue) ||
                              0) * parseInt(p.quantity)
                        : 0;
                }
            }
        });

        return quantity;
    };

    const hasDrawers = productConfig.find(
        (c) => c.name?.toLowerCase() == 'drawers'
    );

    const hasDoors = productConfig.find(
        (c) => c.name?.toLowerCase() == 'doors'
    );

    const hasFridgeDoor = productConfig
        .find((c) => c.name?.toLowerCase() == 'doors')
        ?.fieldsets?.find((fs) => fs.name == 'door_fields_fridge');

    const hasSingleFridgeDoor =
        !hasFridgeDoor && productName?.toLowerCase().indexOf('fridge') > -1;

    const partsDescriptionItems = useMemo<string[]>(() => {
        if (groupedProductParts.size > 0) {
            const groupedParts: string[] = [];
            groupedProductParts.forEach((parts) => {
                let part = ` - ${parts[0].partName}`;

                if (parts.length > 0) {
                    part = ` - ${parts.length} x ${parts[0].partName}`;
                }

                groupedParts.push(part);
            });

            return groupedParts;
        }

        return [];
    }, [groupedProductParts]);

    const cabinetTopsItems = useMemo(
        (): string[] =>
            cabinetDetails?.cabinet_tops
                ?.filter((top) => Number(top.id) == selectedTopId)
                .map((top) => `- ${top?.name}`),
        [cabinetDetails?.cabinet_tops, selectedTopId]
    );

    const shelvesItems = useMemo((): string[] => {
        let localShelves: string[] = [];
        const isComplexShelves =
            groupedShelves &&
            typeof shelvesVertical.lower_shelves !== 'undefined';

        if (
            groupedShelves.size > 0 &&
            cabinetDetails?.shelves?.length > 0 &&
            !isComplexShelves
        ) {
            const shelvesText: string[] = [];

            groupedShelves.forEach((shelf) => {
                const shelfText = `- ${shelf.length} x ${
                    shelf.length > 1 ? 'Shelves' : 'Shelf'
                } (${
                    cabinetDetails?.shelves.find(
                        (s: Shelf) => s.id == shelf[0]?.shelf_type
                    )?.name
                })`;

                shelvesText.push(shelfText);
            });

            localShelves = [...localShelves, ...shelvesText];
        } else {
            if (Object.keys(shelvesVertical).length > 0) {
                if (isComplexShelves)
                    localShelves = [
                        ...localShelves,
                        ...getShelvesVerticalComplex(),
                    ];
                else localShelves = [...localShelves, ...getShelvesVertical()];
            }
        }

        return localShelves;
    }, [groupedShelves, cabinetDetails?.shelves]);

    const shelvesItemsDefault = useMemo((): string[] => {
        return cabinetDetails?.shelves?.length > 0 && shelfAmount > 0
            ? [`- ${shelfAmount} x Shelf (Plain)`]
            : Object.keys(shelvesPosition).length > 0
              ? [
                    `- ${shelvesPosition?.cabinet_hori_shelves} x ${
                        Number(shelvesPosition?.cabinet_hori_shelves) > 1
                            ? 'Shelves'
                            : 'Shelf'
                    } (Horizontal)`,
                    ` - ${shelvesPosition?.cabinet_vert_shelves} x ${
                        Number(shelvesPosition?.cabinet_vert_shelves) > 1
                            ? 'Shelves'
                            : 'Shelf'
                    } (Vertical)`,
                ]
              : Object.keys(shelvesVertical).length > 0
                ? getShelvesVertical()
                : [];
    }, [
        shelfAmount,
        cabinetDetails?.shelves,
        shelvesPosition,
        shelvesVertical,
    ]);

    const hardwareItems = useMemo<string[]>(() => {
        if (groupedDrawers.size > 0) {
            const hardwares: string[] = [];

            groupedDrawers.forEach((drawer) => {
                const hardware = `- ${drawer?.length} x ${
                    !supplyHardware ? 'Drillings for ' : ''
                } ${drawer[0]?.name}`;

                hardwares.push(hardware);
            });

            return hardwares;
        }

        return [];
    }, [groupedDrawers, supplyHardware]);

    const drawerPartsItems = useMemo<string[]>(() => {
        if (groupedParts.size > 0) {
            const partItems: string[] = [];

            groupedParts.forEach((part) => {
                const partItem = `- ${part?.length} x ${part[0]?.part_name}`;

                partItems.push(partItem);
            });

            return partItems;
        }

        return [];
    }, [groupedParts]);

    const drawerHardwaresItems = useMemo<string[]>(() => {
        if (groupedHardwares?.size > 0) {
            const hardwareItems: string[] = [];

            groupedHardwares.forEach((hardware) => {
                const totalQuantity = parseHardwareQuantity(hardware ?? []);
                if (totalQuantity <= 0) {
                    return;
                }

                const hardwareItem = ` - ${totalQuantity} x ${hardware[0]?.hardwareName}`;

                hardwareItems.push(hardwareItem);
            });

            return hardwareItems;
        }
        return [];
    }, [groupedHardwares]);

    const has3Doors = productName?.indexOf('L Shape 3 Door') > -1;

    const noHingeStyle =
        cabinetDetails?.hinges && cabinetDetails?.hinges[0]
            ? cabinetDetails.hinges[0][0] == 21
            : false;

    const hingeMultiplyAmount = has3Doors
        ? 3
        : hasDoors && hasDrawers && !isInnerDrawer
          ? productData?.doors?.length - selectedDrawers?.length
          : productData?.doors?.length;

    const hingeAmount =
        productData?.doors?.length > 1
            ? hingeMultiplyAmount *
              (selectedHinge?.door_hinge_amount
                  ? selectedHinge?.door_hinge_amount
                  : 1)
            : (selectedHinge?.door_hinge_amount_1 && !hasSingleFridgeDoor
                  ? selectedHinge?.door_hinge_amount +
                    (hasFridgeDoor ? selectedHinge?.door_hinge_amount_1 : 0)
                  : selectedHinge?.door_hinge_amount) || 2;

    const hingesItems = useMemo(
        (): string[] => [
            `- ${hingeAmount} x ${
                selectedHinge.drill_only_hinge_name ||
                drillOnlyHingeDetails?.name
            }`,
        ],
        [productData, selectedHinge, drillOnlyHingeDetails]
    );

    const hingesItemsDefault = useMemo(
        (): string[] => [
            `- ${hingeAmount} x Drillings for ${
                selectedHinge.drill_only_hinge_name ||
                drillOnlyHingeDetails?.name ||
                'Blum Inserta Hinges'
            }`,
        ],
        [productData, selectedHinge, drillOnlyHingeDetails]
    );

    const drawerFacesItems = useMemo(
        (): string[] => [
            ...(hasDrawers &&
            !isInnerDrawer &&
            (selectedDrawers?.length || drawerAmount)
                ? [
                      ` - ${selectedDrawers?.length || drawerAmount} x ${
                          selectedDrawers?.length > 1 ? 'Drawers' : 'Drawer'
                      } (${door?.name})`,
                  ]
                : []),
            ...(hasDoors && productData?.doors?.length
                ? [
                      ` - ${
                          has3Doors
                              ? 3
                              : hasDrawers && !isInnerDrawer
                                ? productData?.doors?.length -
                                  selectedDrawers?.length
                                : hasFridgeDoor
                                  ? productData?.doors?.length + 1
                                  : productData?.doors?.length
                      } x ${
                          productData?.doors?.length > 1 ? 'Doors' : 'Door'
                      } (${door?.name})`,
                  ]
                : []),
        ],
        [
            hasDrawers,
            hasDoors,
            productData?.doors,
            selectedDrawers,
            isInnerDrawer,
            drawerAmount,
        ]
    );

    const {productHardwareItems, hardwareQuantity} = useMemo(() => {
        let hardwareQuantity: number;
        const productHardwareItems: string[] = [];

        if (groupedProductHardwares.size > 0) {
            groupedProductHardwares.forEach((hardware) => {
                const isAdjustableLegs = Number(hardware[0]?.hardwareId) == 283;

                if (
                    (!hasAdjustableLegs || hasAdjustableLegs == 0) &&
                    isAdjustableLegs
                ) {
                    return null;
                }

                hardwareQuantity = parseHardwareQuantity(hardware);

                // NOTE: This is done manually for now, may be right
                // way to do this would be get proper information from
                // hardware db later on like formula for quantity rather
                // than a static number
                if (
                    isInnerDrawer &&
                    hardware[0]?.hardwareName
                        .toLowerCase()
                        .includes('inner drawer')
                ) {
                    hardwareQuantity = drawerAmount;
                }

                const hardwareText = `- ${hardwareQuantity} x ${
                    !supplyHardware
                        ? hardwareQuantity > 1
                            ? 'Drillings for '
                            : 'Drilling for '
                        : ''
                }${hardware[0]?.hardwareName}`;

                productHardwareItems.push(hardwareText);
            });
        }

        return {
            productHardwareItems: productHardwareItems.filter((h) => h),
            hardwareQuantity,
        };
    }, [
        groupedProductHardwares,
        hasAdjustableLegs,
        supplyHardware,
        isInnerDrawer,
        drawerAmount,
    ]);

    useEffect(() => {
        // NOTE: this was being called from memo on line 500,
        // which is violation of react lifecycle. we should
        // not update state from within memos. Check if this logic
        // is right after the update. Please fix is not.
        if (typeof hardwareQuantity !== 'undefined') {
            setAdjustableLegsQuantity(hardwareQuantity);
        }
    }, [hardwareQuantity]);

    useEffect(() => {
        const innerDrawerItems = productHardwareItems.filter(
            (phi) => phi.indexOf('Inner Drawer') > -1
        );
        if (innerDrawerItems?.length > 0)
            dispatch(setBaseInnerDrawerFaceHeight(innerDrawerItems));
    }, [productHardwareItems]);

    const productInclusionList: ProductInclusionType[] = [
        {
            title: 'Parts',
            hidden: isNonSupplyCarcase,
            items: [
                ...(partsDescriptionItems ? partsDescriptionItems : []),
                ...(cabinetTopsItems ? cabinetTopsItems : []),
                ...(shelvesItems?.length > 0 && !hasSimpleShelves
                    ? shelvesItems
                    : shelvesItemsDefault),
            ],
        },
        {
            title: 'Hardware',
            hidden: false,
            items: [
                ...hardwareItems,
                ...drawerPartsItems,
                ...drawerHardwaresItems,
                ...(!noHingeStyle && hasDoors
                    ? !supplyHardware &&
                      (selectedHinge.door_hinge_amount ||
                          drillOnlyHingeDetails?.name)
                        ? hingesItemsDefault
                        : selectedHinge &&
                            (selectedHinge.door_hinge_amount ||
                                drillOnlyHingeDetails?.name)
                          ? hingesItems
                          : []
                    : []),
            ],
        },
        {
            title:
                (hasDrawers
                    ? hasDoors
                        ? 'Door and Drawer'
                        : 'Drawer'
                    : 'Door') + ' Faces',
            hidden: isNonSupply || (!hasDrawers && !hasDoors),
            items: [...drawerFacesItems],
        },
        {
            title: 'Others',
            hidden: false,
            items: [...productHardwareItems],
        },
    ];

    return {
        productInclusionList,
    };
};

export default useProductInclusion;
